/**
 * One place that turns an auth failure into something a person can read.
 *
 * WHY THIS EXISTS. Three separate problems, all of them visible to customers:
 *
 * 1. /auth/callback put the RAW Supabase message into the redirect URL
 *    (`?e=${error.message}`) and the login page rendered it verbatim. A user who
 *    started Google sign-in in one browser and finished in another was shown:
 *
 *      "PKCE code verifier not found in storage. This can happen if the auth
 *       flow was initiated in a different browser or device, or if the storage
 *       was cleared. For SSR frameworks (Next.js, SvelteKit, etc.), use
 *       @supabase/ssr on both the server and client to store the code verifier
 *       in cookies."
 *
 *    That is a message for whoever wrote the app, in a red box, in the middle of
 *    signing in. It also put internal detail into a URL that gets shared, logged
 *    and screenshotted.
 *
 * 2. The client fell back to `setError(error.message)` in several places, so
 *    Supabase's English strings appeared on the Italian, Spanish, Russian and
 *    Portuguese login pages.
 *
 * 3. Several messages were hardcoded in English even though a translated key
 *    for exactly that message already existed.
 *
 * So: failures are classified into a small set of STABLE CODES here, the codes
 * travel in URLs and component state, and the UI renders a translated sentence
 * for each one. The raw message keeps going to the server log and the durable
 * activity log, which is where it is actually useful.
 *
 * ADDING A CODE: add it to AuthErrorCode, add a matcher below, and add
 * `errors.<camelCase>` to all seven auth.json files. `pnpm check:auth-messages`
 * fails the build if a code has no message in every locale.
 */

export const AUTH_ERROR_CODES = [
  "WRONG_PASSWORD",
  "NO_ACCOUNT_FOUND",
  "EMAIL_EXISTS",
  "GOOGLE_ONLY",
  "GOOGLE_COLLISION",
  "EMAIL_NOT_CONFIRMED",
  "WEAK_PASSWORD",
  "SAME_PASSWORD",
  "RATE_LIMITED",
  "LINK_EXPIRED",
  "OAUTH_DIFFERENT_BROWSER",
  "OAUTH_CANCELLED",
  "OAUTH_NO_CODE",
  "SESSION_MISSING",
  "SERVICE_UNAVAILABLE",
  "ACCESS_NOT_GRANTED",
  "AUTH_REQUIRED",
  "RESEND_FAILED",
  "GOOGLE_SIGN_IN_FAILED",
  "PASSWORDS_DONT_MATCH",
  "UNEXPECTED",
] as const;

export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[number];

/** The auth.json key each code renders through. */
export const AUTH_ERROR_MESSAGE_KEY: Record<AuthErrorCode, string> = {
  WRONG_PASSWORD: "wrongPassword",
  NO_ACCOUNT_FOUND: "noAccountFound",
  EMAIL_EXISTS: "emailExists",
  GOOGLE_ONLY: "googleOnly",
  GOOGLE_COLLISION: "googleCollision",
  EMAIL_NOT_CONFIRMED: "emailNotConfirmed",
  WEAK_PASSWORD: "passwordTooShort",
  SAME_PASSWORD: "samePassword",
  RATE_LIMITED: "rateLimited",
  LINK_EXPIRED: "linkExpired",
  OAUTH_DIFFERENT_BROWSER: "oauthDifferentBrowser",
  OAUTH_CANCELLED: "oauthCancelled",
  OAUTH_NO_CODE: "oauthNoCode",
  SESSION_MISSING: "sessionMissing",
  SERVICE_UNAVAILABLE: "serviceUnavailable",
  ACCESS_NOT_GRANTED: "accessNotGranted",
  AUTH_REQUIRED: "authRequired",
  RESEND_FAILED: "resendFailed",
  GOOGLE_SIGN_IN_FAILED: "googleSignInFailed",
  PASSWORDS_DONT_MATCH: "passwordsDontMatch",
  UNEXPECTED: "unexpectedError",
};

function isCode(value: string): value is AuthErrorCode {
  return (AUTH_ERROR_CODES as readonly string[]).includes(value);
}

/**
 * True when the browser could not reach Supabase at all. Kept separate because
 * it is the one failure where "try again in a few minutes" is the right advice
 * and the user has done nothing wrong.
 */
export function isNetworkFailure(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes("failed to fetch") ||
    m.includes("networkerror") ||
    m.includes("network request failed") ||
    m.includes("timeout") ||
    m.includes("timed out") ||
    m.includes("aborted") ||
    m.includes("econnrefused") ||
    m.includes("service unavailable") ||
    m.includes("upstream connect")
  );
}

/**
 * Classify a Supabase auth error into a stable code.
 *
 * Matching is on substrings of the message rather than on error codes because
 * supabase-js does not expose a stable code for most of these, and the messages
 * have been consistent across versions. Anything unrecognised becomes
 * UNEXPECTED, which is the safe default: the user gets "something went wrong,
 * try again", not a stack trace.
 */
export function classifyAuthError(
  raw: string | null | undefined,
  status?: number
): AuthErrorCode {
  if (!raw) return "UNEXPECTED";

  // A code we produced ourselves, round-tripping through a URL.
  if (isCode(raw)) return raw;

  const m = raw.toLowerCase();

  if (raw === "no_code") return "OAUTH_NO_CODE";
  if (isNetworkFailure(m)) return "SERVICE_UNAVAILABLE";

  // The PKCE family. All of them mean the same thing to a person: you finished
  // signing in somewhere other than where you started, or your browser cleared
  // the half-finished attempt.
  if (
    m.includes("code verifier") ||
    m.includes("code challenge") ||
    m.includes("pkce") ||
    m.includes("invalid flow state") ||
    m.includes("flow state not found") ||
    m.includes("flow_state_not_found")
  ) {
    return "OAUTH_DIFFERENT_BROWSER";
  }

  if (m.includes("access_denied") || m.includes("access denied") || m.includes("cancelled") || m.includes("canceled")) {
    return "OAUTH_CANCELLED";
  }

  if (
    m.includes("token has expired") ||
    m.includes("otp_expired") ||
    m.includes("otp has expired") ||
    m.includes("email link is invalid or has expired") ||
    m.includes("invalid or has expired")
  ) {
    return "LINK_EXPIRED";
  }

  if (m.includes("invalid login credentials") || m.includes("invalid_credentials")) {
    return "WRONG_PASSWORD";
  }

  if (m.includes("email not confirmed") || m.includes("email_not_confirmed")) {
    return "EMAIL_NOT_CONFIRMED";
  }

  if (
    m.includes("already registered") ||
    m.includes("already been registered") ||
    m.includes("already exists") ||
    m.includes("already been taken") ||
    m.includes("user already") ||
    status === 409
  ) {
    return "EMAIL_EXISTS";
  }

  if (
    m.includes("different from the old password") ||
    m.includes("same as old password") ||
    m.includes("same_password") ||
    m.includes("should be different")
  ) {
    return "SAME_PASSWORD";
  }

  if (m.includes("password should be at least") || m.includes("weak_password") || m.includes("password is too weak")) {
    return "WEAK_PASSWORD";
  }

  if (
    m.includes("rate limit") ||
    m.includes("you can only request this after") ||
    m.includes("too many requests") ||
    status === 429
  ) {
    return "RATE_LIMITED";
  }

  if (m.includes("auth session missing") || m.includes("session_not_found") || m.includes("session missing")) {
    return "SESSION_MISSING";
  }

  return "UNEXPECTED";
}

/**
 * Codes the login page should treat as "this is fixable by signing in with
 * Google instead", so it can show the Google button prominently.
 */
export function suggestsGoogle(code: AuthErrorCode): boolean {
  return code === "GOOGLE_ONLY" || code === "GOOGLE_COLLISION";
}

/**
 * Codes where offering "reset your password" is the useful next step.
 */
export function suggestsPasswordReset(code: AuthErrorCode): boolean {
  return code === "WRONG_PASSWORD";
}
